import { type Ref, ref, watch } from 'vue';

/**
 * Tracks whether a scroll area has hidden content above and/or below, so the
 * caller can show edge fades only when they hint at something scrollable.
 */
export function useScrollFades(
  scrollEl: Ref<HTMLElement | null>,
  contentEl?: Ref<HTMLElement | null>,
): { hasContentAbove: Ref<boolean>; hasContentBelow: Ref<boolean>; update: () => void } {
  const hasContentAbove = ref(false);
  const hasContentBelow = ref(false);

  function update(): void {
    const el = scrollEl.value;
    if (!el) {
      return;
    }
    hasContentAbove.value = el.scrollTop > 0;
    hasContentBelow.value = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
  }

  // Re-attaches whenever the elements change: callers may render the scroll element conditionally
  // (v-if) while the component itself stays mounted.
  watch(
    [scrollEl, contentEl ?? ref(null)],
    ([scroll, content], _prev, onCleanup) => {
      let resizeObserver: ResizeObserver | undefined;
      scroll?.addEventListener('scroll', update, { passive: true });
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(update);
        if (scroll) {
          resizeObserver.observe(scroll);
        }
        if (content) {
          resizeObserver.observe(content);
        }
      }
      update();
      onCleanup(() => {
        scroll?.removeEventListener('scroll', update);
        resizeObserver?.disconnect();
      });
    },
    { immediate: true, flush: 'post' },
  );

  return { hasContentAbove, hasContentBelow, update };
}
